import React, { useState, useEffect } from 'react';
import {
    FileBarChart,
    Download,
    Printer,
    Users,
    Sparkles,
    MessageSquare,
    Mic2,
    Layers,
    Filter,
    Loader2,
    RefreshCw,
    TrendingUp,
    FolderKanban
} from 'lucide-react';
import {
    AreaChart,
    Area,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell
} from 'recharts';
import Swal from 'sweetalert2';
import { API_ENDPOINTS } from '../../config/api';

interface ReportSummary {
    totalPrograms: number;
    activePrograms: number;
    totalQuota: number;
    reportedParticipants: number;
    totalMembers: number;
    sholehahCount: number;
    jofisahCount: number;
    approvedMembers: number;
    pendingMembers: number;
    totalLeaders: number;
    acceptedLeaders: number;
    pendingLeaders: number;
    totalCurhats: number;
    approvedCurhats: number;
    pendingCurhats: number;
    answeredCurhats: number;
    totalSpeakers: number;
    totalArticles: number;
}

interface MonthlyGrowth {
    month: string;
    sholehah: number;
    jofisah: number;
    leaders: number;
    total: number;
}

interface DomicileItem {
    name: string;
    count: number;
}

interface GenerationItem {
    name: string;
    count: number;
}

interface CategoryItem {
    name: string;
    count: number;
}

interface LeaderFunnelItem {
    stage: string;
    count: number;
    color: string;
}

interface ProgramPerformanceItem {
    id: number;
    title: string;
    category: string;
    date: string;
    status: string;
    quota: number;
    participants_count: number;
    tor_status: boolean;
    doc_status: boolean;
    notes: string | null;
}

const ReportsAdmin: React.FC = () => {
    const [summary, setSummary] = useState<ReportSummary | null>(null);
    const [monthlyGrowth, setMonthlyGrowth] = useState<MonthlyGrowth[]>([]);
    const [domicileData, setDomicileData] = useState<DomicileItem[]>([]);
    const [generationData, setGenerationData] = useState<GenerationItem[]>([]);
    const [categoryData, setCategoryData] = useState<CategoryItem[]>([]);
    const [leaderFunnel, setLeaderFunnel] = useState<LeaderFunnelItem[]>([]);
    const [programPerformance, setProgramPerformance] = useState<ProgramPerformanceItem[]>([]);
    const [rawMembers, setRawMembers] = useState<any[]>([]);
    const [rawLeaders, setRawLeaders] = useState<any[]>([]);

    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'overview' | 'programs' | 'leaders' | 'speakers'>('overview');
    const [periodFilter, setPeriodFilter] = useState<'all' | 'month' | 'quarter' | 'year'>('all');
    const [communityFilter, setCommunityFilter] = useState<'all' | 'Sholehah' | 'Jofisah'>('all');
    const [exportType, setExportType] = useState<string>('members');

    const fetchReportData = async () => {
        setIsLoading(true);
        try {
            const [reportRes, sholehahRes, jofisahRes, leaderRes] = await Promise.all([
                fetch(`${API_ENDPOINTS.reports}/summary`),
                fetch(`${API_ENDPOINTS.members}/sholehah`),
                fetch(`${API_ENDPOINTS.members}/jofisah`),
                fetch(API_ENDPOINTS.leaders)
            ]);

            if (reportRes.ok) {
                const data = await reportRes.json();
                setSummary(data.summary);
                setMonthlyGrowth(data.monthlyGrowth || []);
                setDomicileData(data.domicileData || []);
                setGenerationData(data.generationData || []);
                setCategoryData(data.categoryData || []);
                setLeaderFunnel(data.leaderFunnel || []);
                setProgramPerformance(data.programPerformance || []);
            }

            const sholehah = sholehahRes.ok ? await sholehahRes.json() : [];
            const jofisah = jofisahRes.ok ? await jofisahRes.json() : [];
            const leaders = leaderRes.ok ? await leaderRes.json() : [];

            const combinedMembers = [
                ...(Array.isArray(sholehah) ? sholehah.map((m: any) => ({ ...m, community: 'Sholehah' })) : []),
                ...(Array.isArray(jofisah) ? jofisah.map((m: any) => ({ ...m, community: 'Jofisah' })) : [])
            ];

            setRawMembers(combinedMembers);
            setRawLeaders(Array.isArray(leaders) ? leaders : []);

        } catch (error) {
            console.error('Gagal mengambil data laporan:', error);
            Swal.fire({
                icon: 'error',
                title: 'Gagal Memuat Laporan',
                text: 'Terjadi kendala saat mengambil data analitik dari server.',
                confirmButtonColor: '#0F5B30'
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchReportData();
    }, []);

    // Filter anggota berdasarkan pilihan komunitas & periode
    const filteredMembers = rawMembers.filter(m => {
        if (communityFilter !== 'all' && m.community !== communityFilter) return false;

        if (periodFilter !== 'all' && m.created_at) {
            const memberDate = new Date(m.created_at);
            const now = new Date();
            if (periodFilter === 'month') {
                return memberDate.getMonth() === now.getMonth() && memberDate.getFullYear() === now.getFullYear();
            } else if (periodFilter === 'quarter') {
                const diffDays = (now.getTime() - memberDate.getTime()) / (1000 * 3600 * 24);
                return diffDays <= 90;
            } else if (periodFilter === 'year') {
                return memberDate.getFullYear() === now.getFullYear();
            }
        }
        return true;
    });

    // Helper Ekspor CSV dengan UTF-8 BOM untuk Microsoft Excel & Google Sheets
    const handleExportCSV = (type: string) => {
        let headers: string[] = [];
        let rows: string[][] = [];
        let fileName = `khazanah_laporan_${type}_${new Date().toISOString().slice(0, 10)}.csv`;

        if (type === 'members') {
            headers = ['No', 'Komunitas', 'Nama Lengkap', 'Jenis Kelamin', 'No. WhatsApp', 'Generasi', 'Aktivitas', 'Domisili', 'Minat Leader', 'Status', 'Tanggal Daftar'];
            rows = filteredMembers.map((m, idx) => [
                (idx + 1).toString(),
                m.community || 'Umum',
                `"${(m.name || '').replace(/"/g, '""')}"`,
                m.gender || '-',
                `"${m.phone || ''}"`,
                m.generation || '-',
                `"${(m.activity || '').replace(/"/g, '""')}"`,
                `"${(m.domicile || '').replace(/"/g, '""')}"`,
                m.is_interested_leader ? 'Ya' : 'Tidak',
                m.status || 'pending',
                m.created_at ? new Date(m.created_at).toLocaleDateString('id-ID') : '-'
            ]);
        } else if (type === 'programs') {
            headers = ['No', 'Judul Program', 'Kategori', 'Tanggal Pelaksanaan', 'Status', 'Target Kuota', 'Realisasi Peserta', 'Status TOR', 'Dokumentasi', 'Catatan'];
            rows = programPerformance.map((p, idx) => [
                (idx + 1).toString(),
                `"${(p.title || '').replace(/"/g, '""')}"`,
                p.category || 'Umum',
                p.date ? new Date(p.date).toLocaleString('id-ID') : '-',
                p.status || '-',
                (p.quota || 0).toString(),
                (p.participants_count || 0).toString(),
                p.tor_status ? 'Siap' : 'Belum',
                p.doc_status ? 'Lengkap' : 'Belum',
                `"${(p.notes || '').replace(/"/g, '""')}"`
            ]);
        } else if (type === 'leaders') {
            headers = ['No', 'Nama Lengkap', 'Panggilan', 'Email', 'No. HP', 'Domisili', 'Aktivitas', 'Keahlian', 'Status Rekrutmen', 'Tanggal Daftar'];
            rows = rawLeaders.map((l, idx) => [
                (idx + 1).toString(),
                `"${(l.full_name || '').replace(/"/g, '""')}"`,
                `"${(l.nickname || '').replace(/"/g, '""')}"`,
                l.email || '-',
                `"${l.phone || ''}"`,
                `"${(l.domicile || '').replace(/"/g, '""')}"`,
                `"${(l.activity || '').replace(/"/g, '""')}"`,
                `"${(l.skills || '').replace(/"/g, '""')}"`,
                l.status || 'pending',
                l.created_at ? new Date(l.created_at).toLocaleDateString('id-ID') : '-'
            ]);
        }

        const csvContent = '\uFEFF' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        Swal.fire({
            icon: 'success',
            title: 'Ekspor Berhasil!',
            text: `File "${fileName}" telah diunduh.`,
            confirmButtonColor: '#0F5B30',
            timer: 2000
        });
    };

    const handlePrintReport = () => {
        window.print();
    };

    if (isLoading) {
        return (
            <div className="flex h-[70vh] flex-col items-center justify-center text-gray-400">
                <Loader2 className="animate-spin text-emerald mb-3" size={42} />
                <span className="font-outfit font-bold text-base text-gray-700">Mempersiapkan Laporan & Analitik Khazanah...</span>
                <span className="text-xs text-gray-400 mt-1">Mengagregasi data peserta, kegiatan, dan relawan</span>
            </div>
        );
    }

    const memberApprovalRate = summary && summary.totalMembers > 0
        ? Math.round((summary.approvedMembers / summary.totalMembers) * 100)
        : 0;

    const leaderAcceptanceRate = summary && summary.totalLeaders > 0
        ? Math.round((summary.acceptedLeaders / summary.totalLeaders) * 100)
        : 0;

    const curhatResolutionRate = summary && summary.totalCurhats > 0
        ? Math.round((summary.answeredCurhats / summary.totalCurhats) * 100)
        : 0;

    return (
        <div className="space-y-8 font-sans print:p-0">

            {/* HEADER & TOP CONTROLS (Hidden on Print) */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-gray-200/60 pb-6 print:hidden">
                <div>
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-emerald/10 text-emerald flex items-center justify-center border border-emerald/20 shadow-sm">
                            <FileBarChart size={26} />
                        </div>
                        <div>
                            <h1 className="text-2xl lg:text-3xl font-black font-outfit text-gray-900 tracking-tight">
                                Laporan & Analitik Khazanah
                            </h1>
                            <p className="text-xs lg:text-sm text-gray-500 font-medium">
                                Ringkasan komprehensif performa dakwah, pertumbuhan anggota, dan metrik program
                            </p>
                        </div>
                    </div>
                </div>

                {/* Top Action Buttons */}
                <div className="flex flex-wrap items-center gap-3">
                    <button
                        onClick={fetchReportData}
                        className="p-2.5 rounded-xl border border-gray-200 bg-white text-gray-600 hover:text-emerald hover:border-emerald/40 transition-colors shadow-sm"
                        title="Segarkan Data"
                    >
                        <RefreshCw size={18} />
                    </button>

                    <button
                        onClick={handlePrintReport}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 font-bold text-xs lg:text-sm shadow-sm transition-all"
                    >
                        <Printer size={16} />
                        <span>Cetak / PDF</span>
                    </button>

                    <div className="flex items-center gap-1.5 bg-emerald text-white p-1 rounded-xl shadow-sm">
                        <select
                            value={exportType}
                            onChange={(e) => setExportType(e.target.value)}
                            className="bg-emerald text-white font-bold text-xs py-1.5 px-3 rounded-lg border-none outline-none cursor-pointer focus:ring-0"
                        >
                            <option value="members" className="text-gray-900 bg-white">Ekspor Data Anggota</option>
                            <option value="programs" className="text-gray-900 bg-white">Ekspor Data Program</option>
                            <option value="leaders" className="text-gray-900 bg-white">Ekspor Data Leaders</option>
                        </select>
                        <button
                            onClick={() => handleExportCSV(exportType)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-gold hover:bg-gold/90 text-gray-900 font-black rounded-lg text-xs transition-all"
                        >
                            <Download size={14} />
                            <span>Unduh CSV</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* PRINT ONLY HEADER */}
            <div className="hidden print:block mb-8 border-b-2 border-emerald pb-4">
                <h1 className="text-2xl font-black font-outfit text-gray-900">LAPORAN EKSEKUTIF KHAZANAH</h1>
                <p className="text-sm text-gray-600">Dicetak pada: {new Date().toLocaleDateString('id-ID', { dateStyle: 'full' })}</p>
            </div>

            {/* FILTER & PERIOD TOOLBAR (Hidden on Print) */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex flex-wrap items-center justify-between gap-4 print:hidden">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5 mr-1">
                        <Filter size={14} /> Filter:
                    </span>

                    {/* Filter Periode */}
                    <div className="flex bg-gray-100 p-1 rounded-xl text-xs font-bold">
                        <button
                            onClick={() => setPeriodFilter('all')}
                            className={`px-3 py-1.5 rounded-lg transition-all ${periodFilter === 'all' ? 'bg-white text-emerald shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}
                        >
                            Semua Waktu
                        </button>
                        <button
                            onClick={() => setPeriodFilter('month')}
                            className={`px-3 py-1.5 rounded-lg transition-all ${periodFilter === 'month' ? 'bg-white text-emerald shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}
                        >
                            Bulan Ini
                        </button>
                        <button
                            onClick={() => setPeriodFilter('quarter')}
                            className={`px-3 py-1.5 rounded-lg transition-all ${periodFilter === 'quarter' ? 'bg-white text-emerald shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}
                        >
                            3 Bulan Terakhir
                        </button>
                        <button
                            onClick={() => setPeriodFilter('year')}
                            className={`px-3 py-1.5 rounded-lg transition-all ${periodFilter === 'year' ? 'bg-white text-emerald shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}
                        >
                            Tahun Ini
                        </button>
                    </div>

                    {/* Filter Komunitas */}
                    <div className="flex bg-gray-100 p-1 rounded-xl text-xs font-bold">
                        <button
                            onClick={() => setCommunityFilter('all')}
                            className={`px-3 py-1.5 rounded-lg transition-all ${communityFilter === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}
                        >
                            Semua Komunitas
                        </button>
                        <button
                            onClick={() => setCommunityFilter('Sholehah')}
                            className={`px-3 py-1.5 rounded-lg transition-all ${communityFilter === 'Sholehah' ? 'bg-pink-500 text-white shadow-sm' : 'text-pink-600 hover:text-pink-800'}`}
                        >
                            Sholehah ({summary?.sholehahCount || 0})
                        </button>
                        <button
                            onClick={() => setCommunityFilter('Jofisah')}
                            className={`px-3 py-1.5 rounded-lg transition-all ${communityFilter === 'Jofisah' ? 'bg-tealJofisah text-white shadow-sm' : 'text-tealJofisah hover:text-teal-800'}`}
                        >
                            Jofisah ({summary?.jofisahCount || 0})
                        </button>
                    </div>
                </div>

                <div className="text-xs text-gray-500 font-medium">
                    Menampilkan <strong>{filteredMembers.length}</strong> anggota terfilter
                </div>
            </div>

            {/* KEY METRIC KPI CARDS */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
                {/* 1. Total Anggota */}
                <div className="bg-white rounded-3xl p-6 shadow-sm border border-emerald-900/10 relative overflow-hidden flex flex-col justify-between hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                        <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Basis Komunitas</span>
                        <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald flex items-center justify-center border border-emerald-100">
                            <Users size={20} />
                        </div>
                    </div>
                    <div>
                        <div className="flex items-baseline gap-2">
                            <h3 className="text-3xl font-black font-outfit text-gray-900">{summary?.totalMembers || 0}</h3>
                            <span className="text-xs font-bold text-emerald flex items-center gap-0.5">
                                <TrendingUp size={12} /> {memberApprovalRate}% Approved
                            </span>
                        </div>
                        <p className="text-xs text-gray-500 font-medium mt-1">
                            {summary?.sholehahCount || 0} Sholehah &bull; {summary?.jofisahCount || 0} Jofisah
                        </p>
                    </div>
                    <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-[11px] text-gray-400 font-medium">
                        <span>Menunggu Konfirmasi:</span>
                        <span className="font-bold text-amber-600">{summary?.pendingMembers || 0}</span>
                    </div>
                </div>

                {/* 2. Program & Kuota */}
                <div className="bg-white rounded-3xl p-6 shadow-sm border border-emerald-900/10 relative overflow-hidden flex flex-col justify-between hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                        <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Performa Kegiatan</span>
                        <div className="w-10 h-10 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center border border-blue-100">
                            <FolderKanban size={20} />
                        </div>
                    </div>
                    <div>
                        <div className="flex items-baseline gap-2">
                            <h3 className="text-3xl font-black font-outfit text-gray-900">{summary?.totalPrograms || 0}</h3>
                            <span className="text-xs font-bold text-blue-600">
                                {summary?.activePrograms || 0} Aktif
                            </span>
                        </div>
                        <p className="text-xs text-gray-500 font-medium mt-1">
                            Total Kuota: {summary?.totalQuota || 0} Peserta
                        </p>
                    </div>
                    <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-[11px] text-gray-400 font-medium">
                        <span>Realisasi Kehadiran:</span>
                        <span className="font-bold text-blue-700">{summary?.reportedParticipants || 0} Partisipan</span>
                    </div>
                </div>

                {/* 3. Community Leaders */}
                <div className="bg-white rounded-3xl p-6 shadow-sm border border-emerald-900/10 relative overflow-hidden flex flex-col justify-between hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                        <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Community Leaders</span>
                        <div className="w-10 h-10 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center border border-amber-100">
                            <Sparkles size={20} />
                        </div>
                    </div>
                    <div>
                        <div className="flex items-baseline gap-2">
                            <h3 className="text-3xl font-black font-outfit text-gray-900">{summary?.totalLeaders || 0}</h3>
                            <span className="text-xs font-bold text-amber-700">
                                {leaderAcceptanceRate}% Diterima
                            </span>
                        </div>
                        <p className="text-xs text-gray-500 font-medium mt-1">
                            {summary?.acceptedLeaders || 0} Aktif &bull; {summary?.pendingLeaders || 0} Pending
                        </p>
                    </div>
                    <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-[11px] text-gray-400 font-medium">
                        <span>Minat Divisi Terbanyak:</span>
                        <span className="font-bold text-gray-700">Event & Host</span>
                    </div>
                </div>

                {/* 4. Ruang Curhat & Konsultasi */}
                <div className="bg-white rounded-3xl p-6 shadow-sm border border-emerald-900/10 relative overflow-hidden flex flex-col justify-between hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                        <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Ruang Curhat</span>
                        <div className="w-10 h-10 rounded-2xl bg-purple-50 text-purple-600 flex items-center justify-center border border-purple-100">
                            <MessageSquare size={20} />
                        </div>
                    </div>
                    <div>
                        <div className="flex items-baseline gap-2">
                            <h3 className="text-3xl font-black font-outfit text-gray-900">{summary?.totalCurhats || 0}</h3>
                            <span className="text-xs font-bold text-purple-700">
                                {curhatResolutionRate}% Ditanggapi
                            </span>
                        </div>
                        <p className="text-xs text-gray-500 font-medium mt-1">
                            {summary?.approvedCurhats || 0} Disetujui &bull; {summary?.pendingCurhats || 0} Menunggu
                        </p>
                    </div>
                    <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-[11px] text-gray-400 font-medium">
                        <span>Respon Super Admin:</span>
                        <span className="font-bold text-purple-700">{summary?.answeredCurhats || 0} Solusi</span>
                    </div>
                </div>
            </div>

            {/* TAB NAVIGATION (Hidden on Print) */}
            <div className="border-b border-gray-200 print:hidden">
                <nav className="flex space-x-8">
                    <button
                        onClick={() => setActiveTab('overview')}
                        className={`pb-4 text-sm font-outfit font-bold border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'overview' ? 'border-emerald text-emerald' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
                    >
                        <TrendingUp size={16} /> Ikhtisar & Tren
                    </button>
                    <button
                        onClick={() => setActiveTab('programs')}
                        className={`pb-4 text-sm font-outfit font-bold border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'programs' ? 'border-emerald text-emerald' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
                    >
                        <FolderKanban size={16} /> Laporan Program ({programPerformance.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('leaders')}
                        className={`pb-4 text-sm font-outfit font-bold border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'leaders' ? 'border-emerald text-emerald' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
                    >
                        <Sparkles size={16} /> Pipeline Leaders ({rawLeaders.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('speakers')}
                        className={`pb-4 text-sm font-outfit font-bold border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'speakers' ? 'border-emerald text-emerald' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
                    >
                        <Mic2 size={16} /> Narasumber & Artikel
                    </button>
                </nav>
            </div>

            {/* TAB CONTENT 1: OVERVIEW & VISUAL CHARTS */}
            {(activeTab === 'overview' || window.matchMedia('print').matches) && (
                <div className="space-y-8">
                    {/* Charts Row 1: Monthly Growth Trend */}
                    <div className="bg-white rounded-3xl p-6 lg:p-8 shadow-sm border border-gray-100">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                            <div>
                                <h3 className="text-lg font-black font-outfit text-gray-900">
                                    Tren Pertumbuhan Pendaftar Komunitas
                                </h3>
                                <p className="text-xs text-gray-500 font-medium mt-0.5">
                                    Dinamika penambahan anggota Sholehah dan Jofisah per bulan
                                </p>
                            </div>
                            <div className="flex items-center gap-4 text-xs font-bold">
                                <span className="flex items-center gap-1.5 text-pink-600">
                                    <span className="w-3 h-3 rounded-full bg-pink-500 inline-block"></span> Sholehah
                                </span>
                                <span className="flex items-center gap-1.5 text-tealJofisah">
                                    <span className="w-3 h-3 rounded-full bg-tealJofisah inline-block"></span> Jofisah
                                </span>
                            </div>
                        </div>

                        <div className="h-72 w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={monthlyGrowth} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="sholehahGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#EC4899" stopOpacity={0.4} />
                                            <stop offset="95%" stopColor="#EC4899" stopOpacity={0.0} />
                                        </linearGradient>
                                        <linearGradient id="jofisahGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#1E7F8A" stopOpacity={0.4} />
                                            <stop offset="95%" stopColor="#1E7F8A" stopOpacity={0.0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9CA3AF' }} tickLine={false} axisLine={false} />
                                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#9CA3AF' }} tickLine={false} axisLine={false} />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: '#ffffff',
                                            borderRadius: '16px',
                                            border: '1px solid #F3F4F6',
                                            boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
                                            fontSize: '12px',
                                            fontFamily: 'Outfit'
                                        }}
                                    />
                                    <Area type="monotone" dataKey="sholehah" name="Sholehah" stroke="#EC4899" strokeWidth={3} fillOpacity={1} fill="url(#sholehahGrad)" />
                                    <Area type="monotone" dataKey="jofisah" name="Jofisah" stroke="#1E7F8A" strokeWidth={3} fillOpacity={1} fill="url(#jofisahGrad)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Charts Row 2: Demografi & Kategori */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Top Domisili */}
                        <div className="bg-white rounded-3xl p-6 lg:p-8 shadow-sm border border-gray-100">
                            <div className="mb-6">
                                <h3 className="text-lg font-black font-outfit text-gray-900">
                                    Persebaran Wilayah / Domisili
                                </h3>
                                <p className="text-xs text-gray-500 font-medium mt-0.5">
                                    Kota asal dengan jumlah pendaftar tertinggi
                                </p>
                            </div>

                            {domicileData.length === 0 ? (
                                <div className="h-60 flex items-center justify-center text-gray-400 text-xs font-medium">
                                    Belum ada data domisili pendaftar.
                                </div>
                            ) : (
                                <div className="h-60 w-full">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={domicileData} layout="vertical" margin={{ top: 0, right: 20, left: 20, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F3F4F6" />
                                            <XAxis type="number" tick={{ fontSize: 11, fill: '#9CA3AF' }} tickLine={false} axisLine={false} />
                                            <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#4B5563', fontWeight: 600 }} tickLine={false} axisLine={false} width={80} />
                                            <Tooltip
                                                contentStyle={{
                                                    backgroundColor: '#ffffff',
                                                    borderRadius: '12px',
                                                    border: '1px solid #E5E7EB',
                                                    fontSize: '12px'
                                                }}
                                            />
                                            <Bar dataKey="count" name="Jumlah Pendaftar" fill="#0F5B30" radius={[0, 8, 8, 0]}>
                                                {domicileData.map((_, index) => (
                                                    <Cell key={`cell-${index}`} fill={index === 0 ? '#0F5B30' : index === 1 ? '#18703E' : '#C5A85C'} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </div>

                        {/* Distribusi Generasi & Program */}
                        <div className="bg-white rounded-3xl p-6 lg:p-8 shadow-sm border border-gray-100 flex flex-col justify-between">
                            <div>
                                <div className="mb-6">
                                    <h3 className="text-lg font-black font-outfit text-gray-900">
                                        Komposisi Generasi Pendaftar
                                    </h3>
                                    <p className="text-xs text-gray-500 font-medium mt-0.5">
                                        Perbandingan demografi rentang usia
                                    </p>
                                </div>

                                <div className="space-y-4">
                                    {generationData.map((gen, idx) => {
                                        const total = summary?.totalMembers || 1;
                                        const pct = Math.round((gen.count / total) * 100);
                                        return (
                                            <div key={idx} className="space-y-1.5">
                                                <div className="flex justify-between text-xs font-bold">
                                                    <span className="text-gray-700">{gen.name}</span>
                                                    <span className="text-emerald">{gen.count} orang ({pct}%)</span>
                                                </div>
                                                <div className="w-full bg-gray-100 h-2.5 rounded-full overflow-hidden">
                                                    <div
                                                        className={`h-full rounded-full ${idx === 0 ? 'bg-emerald' : idx === 1 ? 'bg-gold' : 'bg-tealJofisah'}`}
                                                        style={{ width: `${Math.min(100, pct)}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Mini Category Stat */}
                            <div className="mt-8 pt-6 border-t border-gray-100">
                                <span className="text-xs font-bold uppercase tracking-wider text-gray-400 block mb-3">
                                    Kategori Program Terbanyak
                                </span>
                                <div className="flex flex-wrap gap-2">
                                    {categoryData.map((c, i) => (
                                        <div key={i} className="px-3 py-1.5 rounded-xl bg-gray-50 border border-gray-200/60 flex items-center gap-2 text-xs font-bold text-gray-700">
                                            <span>{c.name}</span>
                                            <span className="px-1.5 py-0.5 rounded-md bg-emerald/10 text-emerald text-[10px]">{c.count} Event</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB CONTENT 2: PROGRAM PERFORMANCE REPORT */}
            {activeTab === 'programs' && (
                <div className="bg-white rounded-3xl p-6 lg:p-8 shadow-sm border border-gray-100 space-y-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h3 className="text-lg font-black font-outfit text-gray-900">
                                Rekap Kinerja & Partisipasi Program
                            </h3>
                            <p className="text-xs text-gray-500 font-medium">
                                Data evaluasi kuota, realisasi partisipan, serta kelengkapan administrasi kegiatan
                            </p>
                        </div>
                        <button
                            onClick={() => handleExportCSV('programs')}
                            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald/10 text-emerald hover:bg-emerald/20 font-bold text-xs transition-colors"
                        >
                            <Download size={14} /> Unduh CSV Program
                        </button>
                    </div>

                    {programPerformance.length === 0 ? (
                        <div className="py-16 text-center text-gray-400 text-sm">
                            Belum ada riwayat program yang terdaftar.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-xs">
                                <thead>
                                    <tr className="border-b border-gray-100 text-gray-400 uppercase font-black tracking-wider">
                                        <th className="py-3 px-4">Program & Tema</th>
                                        <th className="py-3 px-4">Kategori</th>
                                        <th className="py-3 px-4">Tanggal</th>
                                        <th className="py-3 px-4">Kuota</th>
                                        <th className="py-3 px-4">Realisasi</th>
                                        <th className="py-3 px-4">Kelengkapan SOP</th>
                                        <th className="py-3 px-4">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50 font-medium text-gray-700">
                                    {programPerformance.map((p) => {
                                        const fillRate = p.quota > 0 ? Math.round((p.participants_count / p.quota) * 100) : 0;
                                        return (
                                            <tr key={p.id} className="hover:bg-gray-50/80 transition-colors">
                                                <td className="py-4 px-4 font-bold text-gray-900">
                                                    {p.title}
                                                </td>
                                                <td className="py-4 px-4">
                                                    <span className="px-2.5 py-1 rounded-lg bg-emerald/10 text-emerald font-bold">
                                                        {p.category}
                                                    </span>
                                                </td>
                                                <td className="py-4 px-4 text-gray-500">
                                                    {p.date ? new Date(p.date).toLocaleDateString('id-ID', { dateStyle: 'medium' }) : '-'}
                                                </td>
                                                <td className="py-4 px-4 font-bold">
                                                    {p.quota} orang
                                                </td>
                                                <td className="py-4 px-4">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-bold text-gray-900">{p.participants_count}</span>
                                                        <span className="text-[10px] text-gray-400">({fillRate}%)</span>
                                                    </div>
                                                </td>
                                                <td className="py-4 px-4">
                                                    <div className="flex items-center gap-2">
                                                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${p.tor_status ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                                                            TOR {p.tor_status ? '✓' : '✗'}
                                                        </span>
                                                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${p.doc_status ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                                                            DOC {p.doc_status ? '✓' : '✗'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="py-4 px-4">
                                                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase ${p.status === 'active' ? 'bg-emerald-100 text-emerald-800' : p.status === 'full' ? 'bg-rose-100 text-rose-800' : 'bg-gray-100 text-gray-600'}`}>
                                                        {p.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* TAB CONTENT 3: LEADERS PIPELINE REPORT */}
            {activeTab === 'leaders' && (
                <div className="space-y-8">
                    {/* Funnel Visual */}
                    <div className="bg-white rounded-3xl p-6 lg:p-8 shadow-sm border border-gray-100">
                        <div className="mb-6">
                            <h3 className="text-lg font-black font-outfit text-gray-900">
                                Pipeline Rekrutmen Community Leaders
                            </h3>
                            <p className="text-xs text-gray-500 font-medium">
                                Tahapan konversi pendaftar relawan hingga menjadi Community Leader aktif
                            </p>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                            {leaderFunnel.map((item, idx) => (
                                <div key={idx} className="p-4 rounded-2xl bg-gray-50 border border-gray-100 flex flex-col justify-between">
                                    <span className="text-[11px] font-bold text-gray-500">{item.stage}</span>
                                    <div className="mt-2 flex items-baseline gap-2">
                                        <span className="text-2xl font-black font-outfit text-gray-900">{item.count}</span>
                                        <span className="text-[10px] text-gray-400">orang</span>
                                    </div>
                                    <div className="w-full bg-gray-200 h-1.5 rounded-full mt-3 overflow-hidden">
                                        <div className="h-full rounded-full" style={{ backgroundColor: item.color, width: '100%' }}></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Table of Leaders */}
                    <div className="bg-white rounded-3xl p-6 lg:p-8 shadow-sm border border-gray-100">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                            <div>
                                <h3 className="text-lg font-black font-outfit text-gray-900">
                                    Daftar Kandidat & Community Leaders
                                </h3>
                                <p className="text-xs text-gray-500 font-medium">
                                    Detail pelamar, domisili, keahlian, dan status seleksi
                                </p>
                            </div>
                            <button
                                onClick={() => handleExportCSV('leaders')}
                                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald/10 text-emerald hover:bg-emerald/20 font-bold text-xs transition-colors"
                            >
                                <Download size={14} /> Unduh CSV Leaders
                            </button>
                        </div>

                        {rawLeaders.length === 0 ? (
                            <div className="py-16 text-center text-gray-400 text-sm">
                                Belum ada data pelamar Community Leaders.
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-xs">
                                    <thead>
                                        <tr className="border-b border-gray-100 text-gray-400 uppercase font-black tracking-wider">
                                            <th className="py-3 px-4">Nama Lengkap</th>
                                            <th className="py-3 px-4">Kontak</th>
                                            <th className="py-3 px-4">Domisili & Usia</th>
                                            <th className="py-3 px-4">Keahlian</th>
                                            <th className="py-3 px-4">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50 font-medium text-gray-700">
                                        {rawLeaders.map((l) => (
                                            <tr key={l.id} className="hover:bg-gray-50/80 transition-colors">
                                                <td className="py-4 px-4">
                                                    <div className="font-bold text-gray-900">{l.full_name}</div>
                                                    <span className="text-[10px] text-gray-400 font-normal">Panggilan: {l.nickname || '-'}</span>
                                                </td>
                                                <td className="py-4 px-4 text-gray-500">
                                                    <div>{l.email}</div>
                                                    <div className="text-[10px] text-emerald">{l.phone}</div>
                                                </td>
                                                <td className="py-4 px-4 text-gray-600">
                                                    <div>{l.domicile}</div>
                                                    <div className="text-[10px] text-gray-400">{l.activity}</div>
                                                </td>
                                                <td className="py-4 px-4">
                                                    <span className="text-gray-800 line-clamp-1 max-w-xs">{l.skills || '-'}</span>
                                                </td>
                                                <td className="py-4 px-4">
                                                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase ${l.status === 'accepted' ? 'bg-green-100 text-green-700' : l.status === 'shortlisted' ? 'bg-indigo-100 text-indigo-700' : l.status === 'interview' ? 'bg-amber-100 text-amber-700' : l.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                                                        {l.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* TAB CONTENT 4: SPEAKERS & ARTICLES */}
            {activeTab === 'speakers' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Ringkasan Narasumber */}
                    <div className="bg-white rounded-3xl p-6 lg:p-8 shadow-sm border border-gray-100 space-y-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-black font-outfit text-gray-900">
                                    Narasumber & Pemateri
                                </h3>
                                <p className="text-xs text-gray-500 font-medium">
                                    Database asatidz dan pembicara kegiatan Khazanah
                                </p>
                            </div>
                            <div className="w-10 h-10 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center">
                                <Mic2 size={20} />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="p-4 rounded-2xl bg-gray-50 border border-gray-100">
                                <span className="text-xs text-gray-400 font-bold uppercase">Total Narasumber</span>
                                <div className="text-2xl font-black font-outfit text-gray-900 mt-1">{summary?.totalSpeakers || 0}</div>
                            </div>
                            <div className="p-4 rounded-2xl bg-gray-50 border border-gray-100">
                                <span className="text-xs text-gray-400 font-bold uppercase">Kategori Fokus</span>
                                <div className="text-2xl font-black font-outfit text-emerald mt-1">Multi-Topik</div>
                            </div>
                        </div>

                        <p className="text-xs text-gray-500 leading-relaxed">
                            Pengelolaan jadwal, ratecard honorarium, dan relasi narasumber dapat diatur lebih detail melalui menu <strong>Speakers</strong> pada navigasi utama.
                        </p>
                    </div>

                    {/* Ringkasan Artikel & Edukasi */}
                    <div className="bg-white rounded-3xl p-6 lg:p-8 shadow-sm border border-gray-100 space-y-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-black font-outfit text-gray-900">
                                    Publikasi & Artikel Dakwah
                                </h3>
                                <p className="text-xs text-gray-500 font-medium">
                                    Konten literasi keislaman dan self-growth pemuda
                                </p>
                            </div>
                            <div className="w-10 h-10 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
                                <Layers size={20} />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="p-4 rounded-2xl bg-gray-50 border border-gray-100">
                                <span className="text-xs text-gray-400 font-bold uppercase">Total Artikel</span>
                                <div className="text-2xl font-black font-outfit text-gray-900 mt-1">{summary?.totalArticles || 0}</div>
                            </div>
                            <div className="p-4 rounded-2xl bg-gray-50 border border-gray-100">
                                <span className="text-xs text-gray-400 font-bold uppercase">Status Kurasi</span>
                                <div className="text-2xl font-black font-outfit text-blue-600 mt-1">Aktif</div>
                            </div>
                        </div>

                        <p className="text-xs text-gray-500 leading-relaxed">
                            Seluruh artikel yang masuk dari kontributor dapat disetujui, diedit, atau ditolak melalui menu <strong>Artikel</strong> pada sidebar admin.
                        </p>
                    </div>
                </div>
            )}

        </div>
    );
};

export default ReportsAdmin;
